/**
 * Service pour récupérer les données client depuis l'API
 */

import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { CLIENT_DATA_QUERY_CONFIG, SYNTHESIS_QUERY_CONFIG } from "@/lib/queryConfig";
import {
  pilotageDashboardResponseSchema,
  type PilotageClientStatus,
  type PilotageDashboardResponse,
  type PilotageEntryCreatePayload,
  type PilotageEntryUpdatePayload,
  type PilotageRuleCreatePayload,
  type PilotageRuleUpdatePayload,
} from "@/types/pilotageDashboard";
import type { PackAilixConfig, PricingMode } from "@/utils/packAilixPricing";

// ============================================================================
// DEFAULTS MÉTIER (source unique pour transformApiData et consommateurs)
// ============================================================================

export const CLIENT_DATA_DEFAULTS = {
  tjm: 650,
  tjm_moe: 1000,
  annual_cost_percentage: 20.0,
  project_management_percentage: 10.0,
  project_duration_years: 3,
  hourly_rate: 44.0,
  mission_display_mode: 'classic' as const,
  idea_missions_display_mode: 'separate' as const,
} as const;

export type PilotageStage = 1 | 2 | 3 | 4 | 5 | 6;
export type PilotageHealth = "yellow" | "green_up";
export type PilotageSectionKey = "main" | "agents_2025";
export type PilotageItemKind = "project" | "program";
export type PilotageNature = "agent" | "mission" | "module";

/**
 * Helper privé pour inférer un nombre de jours à partir d'un texte de durée
 * Détecte "X semaine(s)" et convertit en jours ouvrés (par défaut 5 jours/semaine)
 * Gère les plages (ex: "2-3 semaines" → moyenne)
 * @param text - Texte contenant potentiellement une durée en semaines
 * @param daysPerWeek - Nombre de jours ouvrés par semaine (défaut: 5)
 * @returns Nombre de jours entier ou null si non parsable
 */
function inferDurationDaysFromText(text: string | null | undefined, daysPerWeek: number = 5): number | null {
  if (!text || typeof text !== 'string') return null;
  
  const normalized = text.toLowerCase().trim();
  
  // Pattern pour "X semaine(s)" ou "X-Y semaine(s)"
  const weekPattern = /(\d+)(?:\s*-\s*(\d+))?\s*semaines?/;
  const match = normalized.match(weekPattern);
  
  if (!match) return null;
  
  const firstNum = parseInt(match[1], 10);
  const secondNum = match[2] ? parseInt(match[2], 10) : null;
  
  if (isNaN(firstNum)) return null;
  
  // Si plage détectée (ex: "2-3 semaines"), prendre la moyenne
  const weeks = secondNum && !isNaN(secondNum) 
    ? (firstNum + secondNum) / 2 
    : firstNum;
  
  // Convertir en jours ouvrés et arrondir
  const days = Math.round(weeks * daysPerWeek);
  
  return days > 0 ? days : null;
}

export interface Mission {
  id: string;
  title: string;
  description: string;
  category: string;
  complexity: string;
  duration: string; // Pour compatibilité avec mockData
  duration_days?: number;
  duration_text?: string;
  price: number;
  team: string; // Pour compatibilité avec mockData  
  team_size?: string;
  dependencies: string[];
  deliverables: string[];
  mission_type?: string;
  type_of_dev?: string; // Type de développement: Automatisation, Module dev, Feature dev, Humain/non dev
  mission_number?: number;
  cost_analysis?: number; // Coûts d'IA en centimes entiers (ex: 13 = 0,13 centimes)
  complete_json?: any;
  roi_data?: any;
  overview_data?: any;
  benefits_data?: any;
  plan_data?: any;
  plan_detaille?: any;  // Plan détaillé généré par le nouveau système
  approach_data?: any;
  Analyse_cahier_des_charges?: any; // Analyse CDC avec description mission et notes/observations
  // Nouveaux champs temporels
  mission_month?: number; // Mois de facturation (1-12)
  mission_year?: number;  // Année de facturation
  // Nouveaux champs pour les idées de missions analysées par IA
  mission_principale?: string;
  resultat_attendu?: string;
  solution_capable_de?: string[];
  // Champ de priorisation
  priorisation?: 'non_prioritized' | 'P0' | 'P1' | 'P2' | 'P3' | 'Px';
  // Champ de versioning
  version_mission?: number | null;
  // Rang de tri intra-priorité (0 = premier)
  priority_rank?: number;
  // Champ de verrouillage du planning
  planning_locked?: boolean;
  // Assignation développeur
  assigned_developer_id?: string | null;
  ailix_developers?: {
    id: string;
    name: string;
    color: string;
  } | null;
  // Champ de statut (en cours / terminé)
  status?: 'in_progress' | 'completed' | null;
  // ID de la catégorie personnalisée
  category_id?: string | null;
  created_at?: string;
  updated_at?: string;
  // Sous-missions (regroupement)
  grouping_mode?: 'solo' | 'group';
  parent_mission_id?: string | null;
  group_discount_percent?: number;
  sub_mission_order?: number;
  sub_mission_presentation?: string;
  sub_missions?: Mission[];
  // Champs pour compatibilité avec l'ancien affichage mockData
  roi?: string;
  timesSaved?: string;
  roiData?: any;
  completeData?: any;
}

export interface Option {
  id: string;
  option_id: string;
  title: string;
  description: string;
  price: number;
  duration: string;
  recurring: boolean;
  // Champs pour le calcul dynamique des options
  calc_percentage?: number;
  calc_coefficient?: number;
  auto_calc_enabled?: boolean;
  is_included?: boolean;
  duration_days?: number;
  option_group?: string;
  maintenance_percentage?: number;
  calculation_info?: string;
  default_selected?: boolean;
  hidden_from_client?: boolean;
}

export interface MissionCategory {
  id: string;
  name: string;
  description?: string;
  icon_name: string;
  color_class: string;
  sort_order: number;
}

export interface ClientData {
  id: string;
  title: string;
  client: string;
  status: string;
  createdAt: string;
  lastUpdated: string;
  executive: any;
  foundations: Mission[];
  missions: Mission[];
  additionalMissions: Mission[];
  options: Option[];
  categories: MissionCategory[];  // Nouvelles catégories personnalisées
  pricingRules: any;
  summary: any;
  tjm: number; // Taux Journalier Moyen du projet (développeurs)
  tjm_moe?: number; // TJM MOE (gestion projet + design-moe) - défaut: 1000€
  annual_cost_percentage?: number; // Pourcentage du coût annuel de maintenance (défaut: 20%)
  project_management_percentage?: number; // Pourcentage de gestion de projet (défaut: 10.0%)
  project_duration_years?: number; // Durée du projet en années (défaut: 3)
  timeline_frise_enabled?: boolean; // Activation de la frise temporelle (défaut: true)
  planning_enabled?: boolean; // Activation de l'affichage du planning détaillé des missions (défaut: true)
  versions_enabled?: boolean; // Activation de l'affichage des versions des missions (défaut: true)
  roi_enabled?: boolean; // Activation de l'affichage des blocs ROI (défaut: true)
  special_offer_enabled?: boolean; // Activation de l'affichage de l'offre spéciale (défaut: false)
  hide_category_badges?: boolean; // Masquer les badges de catégories sur les missions (défaut: false)
  pack_ailix_enabled?: boolean; // Activation du Pack Ailix (défaut: false)
  pack_ailix_config?: PackAilixConfig | null; // Configuration personnalisée Pack Ailix
  cautious_planning_enabled?: boolean; // Activation du planning avec précaution (défaut: false)
  cautious_planning_percentage?: number; // Pourcentage de marge de sécurité (défaut: 20)
  pilotage_enabled?: boolean; // Mode pilotage actif (client signé, params verrouillés)
  pilotage_nb_developers?: number | null; // Nb devs figé pour le pilotage
  pilotage_start_month?: number | null; // Mois de début figé pour le pilotage (1-12)
  pilotage_start_year?: number | null; // Année de début figée pour le pilotage
  finalisation_nb_developers?: number | null; // Nb devs partagé pour Finalisation
  finalisation_start_month?: number | null; // Mois de début partagé pour Finalisation
  finalisation_start_year?: number | null; // Année de début partagée pour Finalisation
  finalisation_offer_mode?: PricingMode | null; // Mode tarifaire partagé pour Finalisation
  finalisation_enabled_prestations?: Record<string, boolean>; // Prestations hybrides activées partagées
  disabled_volume_discounts?: { "50k"?: boolean; "100k"?: boolean; "250k"?: boolean; "350k"?: boolean; "550k"?: boolean }; // Paliers de remises volume désactivés
  hourly_rate?: number; // Coût horaire chargé d'un collaborateur en euros (défaut: 44.0€/heure)
  mission_display_mode?: 'classic' | 'versioning' | 'priority'; // Mode d'affichage des missions (défaut: 'classic')
  idea_missions_display_mode?: 'separate' | 'mixed'; // Mode d'affichage des idées de missions (défaut: 'separate')
  exceptional_discount_percentage?: number; // Pourcentage de remise exceptionnelle
  missions_filters_defaults?: {
    hidden_priorisations?: string[]; // Priorités masquées par défaut (ex: ["P3", "Px"])
  }; // Filtres par défaut pour la vue "Missions & Config"
  homeStatistics?: {
    netBenefit?: string;
    equivalentFTE?: string;
    globalROI?: string;
    amortization?: string;
    projectDurationYears?: number;
    // Legacy (ancien format)
    economiesSur3Ans?: string;
    roiMoyen?: string;
    tempsAmortissement?: string;
  }; // Statistiques pré-calculées de la page d'accueil
}

export interface SynthesisData {
  clientId: string;
  financialData: any;
  missionCategories: any[];
  topROIMissions: any[];
  allMissions: any[];
  kpis: any[];
  updatedAt: string;
}

export interface PricingRequest {
  selectedItems: Array<{
    id: string;
    type: 'mission' | 'option';
    quantity: number;
  }>;
}

export interface PricingResponse {
  subtotal: number;
  discounts: Array<{
    name: string;
    amount: number;
    percentage: number;
  }>;
  totalDiscounts: number;
  total: number;
  estimatedDuration: string;
  itemsDetails: any[];
}

// `duration_days` et `price` retirés le 2026-08-18 (R005-FUITE-CLIENT-001) : la route summary
// n'est pas authentifiée, elle porte la même fuite que le dashboard. Ne pas les remettre.
export interface PilotageSummaryMission {
  id: string;
  title: string;
  team?: string | null;
  priorisation?: Mission["priorisation"];
  status?: Mission["status"];
  assigned_developer_id?: string | null;
  developer_name?: string | null;
  developer_color?: string | null;
}

export interface PilotageSummaryAvailableMission {
  id: string;
  title: string;
  priorisation?: Mission["priorisation"];
}

export interface PilotageSummaryItem {
  id: string;
  project_id: string;
  mission_id: string | null;
  custom_title: string | null;
  title_snapshot: string | null;
  title_override: string | null;
  title: string;
  pilotage_owner_name: string | null;
  pilotage_stage: PilotageStage;
  pilotage_health: PilotageHealth;
  pilotage_caption: string | null;
  pilotage_target_date: string | null;
  pilotage_client_status: PilotageClientStatus | null;
  pilotage_client_status_reason: string | null;
  estimated_delivery: string | null;
  estimated_delivery_source: "manual" | "auto" | null;
  detail_url: string | null;
  section_key: PilotageSectionKey;
  section_title: string | null;
  item_kind: PilotageItemKind;
  nature: PilotageNature | null;
  program_delivered_count: number | null;
  program_pending_count: number | null;
  sort_order: number;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
  mission: PilotageSummaryMission | null;
  subitems: PilotageSummarySubitem[];
  subitem_stats: PilotageSummarySubitemStats;
  // REFONDATION-PILOTAGE-LOT1.3 — comptes servis par le serveur pour l'accueil client
  // (optionnels : le front tolère l'absence — cellules « rien pour l'instant » / « à fixer »).
  waiting_client_count?: number;
  waiting_ailix_count?: number;
  // CDP B2 MAJEUR (REFONDATION-PILOTAGE-LOT2-STORY-2.2, §3.7, 2026-10-04) : nombre
  // d'attentes suspendues par la pause de la ligne. Rend symétrie avec le dashboard
  // (`en_attente_suspended_count`). Optionnel : tolère les réponses backend antérieures
  // au déploiement migration 247.
  waiting_suspended_count?: number;
  next_client_action?: { label: string; since: string | null } | null;
  last_event_date?: string | null;
  // REFONDATION-PILOTAGE-LOT2-STORY-2.2 — Pause à périmètre (§3.7). 10 champs servis
  // par le serveur sur les 2 endpoints (summary + dashboard). `.optional()` : tolère les
  // réponses backend antérieures au déploiement migration 247.
  paused_since?: string | null;
  pause_reason?: string | null;
  pause_decided_by?: string | null;
  pause_review_date?: string | null;
  pause_reviewer_name?: string | null;
  pause_scope?: "project" | "volet" | null;
  pause_scope_note?: string | null;
  pause_no_delivery?: boolean | null;
  pause_subitem_id?: string | null;
  paused_stage?: PilotageStage | null;
  // REFONDATION-PILOTAGE-ACCUEIL-V5 : ensemble et version en production (sous-ticket .2).
  // Optionnels : la page reste correcte sans eux (aucun ensemble, colonne Version vide).
  group_id?: string | null;
  prod_version_label?: string | null;
  prod_version_date?: string | null;
  prod_version_date_precision?: "day" | "month" | null;
}

// REFONDATION-PILOTAGE-ACCUEIL-V5 : ensemble de projets (« Automail », « Gates »), servi par `.2`.
export interface PilotageSummaryGroup {
  id: string;
  title: string;
  detail_url?: string | null;
  sort_order: number;
}

export interface PilotageSummarySubitem {
  id: string;
  summary_item_id: string;
  title: string;
  owner_name: string | null;
  pilotage_stage: PilotageStage;
  pilotage_caption: string | null;
  detail_url: string | null;
  sort_order: number;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface PilotageSummarySubitemStats {
  total: number;
  by_stage: Record<string, number>;
  delivered: number;
  to_scope: number;
  is_multi_stage: boolean;
}

export interface PilotageSummaryResponse {
  ok: boolean;
  project_id: string;
  project: {
    id: string;
    title?: string | null;
    client_name?: string | null;
    pilotage_enabled?: boolean;
  };
  items: PilotageSummaryItem[];
  groups?: PilotageSummaryGroup[];
  available_missions: PilotageSummaryAvailableMission[];
}

export interface PilotageSummaryCreatePayload {
  mission_id?: string | null;
  custom_title?: string | null;
  title_override?: string | null;
  pilotage_owner_name?: string | null;
  pilotage_stage?: PilotageStage;
  pilotage_health?: PilotageHealth;
  pilotage_caption?: string | null;
  pilotage_target_date?: string | null;
  detail_url?: string | null;
  section_key?: PilotageSectionKey;
  section_title?: string | null;
  item_kind?: PilotageItemKind;
  nature?: PilotageNature | null;
  program_delivered_count?: number | null;
  program_pending_count?: number | null;
  sort_order?: number;
}

export interface PilotageSummaryUpdatePayload {
  mission_id?: string | null;
  custom_title?: string | null;
  title_override?: string | null;
  pilotage_owner_name?: string | null;
  pilotage_stage?: PilotageStage;
  pilotage_health?: PilotageHealth;
  pilotage_caption?: string | null;
  pilotage_target_date?: string | null;
  detail_url?: string | null;
  section_key?: PilotageSectionKey;
  section_title?: string | null;
  item_kind?: PilotageItemKind;
  nature?: PilotageNature | null;
  program_delivered_count?: number | null;
  program_pending_count?: number | null;
  sort_order?: number;
  archived?: boolean;
  // run-003 Story 12 — statut client wording-safe (enum en_bonne_voie|livre, D8). Le backend
  // `PilotageSummaryItemUpdateRequest` accepte déjà ces 2 champs (clients.py:1943-1944) ; sans
  // eux ici l'édition du statut depuis le dashboard détail casse le typage (GF-S12.1).
  pilotage_client_status?: PilotageClientStatus | null;
  pilotage_client_status_reason?: string | null;
  // REFONDATION-PILOTAGE-ACCUEIL-V5 : ensemble et version en production (PATCH de `.2`).
  group_id?: string | null;
  prod_version_label?: string | null;
  prod_version_date?: string | null;
  prod_version_date_precision?: "day" | "month" | null;
}

export interface PilotageSummaryItemMutationResponse {
  ok: boolean;
  item: PilotageSummaryItem;
}

export interface PilotageSummarySubitemCreatePayload {
  title: string;
  owner_name?: string | null;
  pilotage_stage?: PilotageStage;
  pilotage_caption?: string | null;
  detail_url?: string | null;
  sort_order?: number;
}

export interface PilotageSummarySubitemUpdatePayload {
  title?: string;
  owner_name?: string | null;
  pilotage_stage?: PilotageStage;
  pilotage_caption?: string | null;
  detail_url?: string | null;
  sort_order?: number;
  archived?: boolean;
}

export interface PilotageSummarySubitemMutationResponse {
  ok: boolean;
  subitem: PilotageSummarySubitem;
}

export const clientDataKeys = {
  root: ['clientData'] as const,
  byClientRoot: (clientId: string) => ['clientData', clientId] as const,
  byClient: (clientId: string, view: 'full' | 'summary') => ['clientData', clientId, view] as const,
  synthesis: (clientId: string) => ['synthesisData', clientId] as const,
  missionDetail: (clientId: string, missionId: string) => ['missionDetail', clientId, missionId] as const,
  pilotageSummary: (clientId: string) => ['clientData', clientId, 'pilotageSummary'] as const,
  pilotageDashboard: (clientId: string, itemId: string) =>
    ['clientData', clientId, 'pilotageDashboard', itemId] as const,
};

/**
 * Service pour gérer les données client
 */
export class ClientDataService {
  
  /**
   * Récupérer toutes les données d'un client (missions, options, etc.)
   */
  static async getClientData(clientId: string, view: 'full' | 'summary' = 'full'): Promise<ClientData> {
    try {
      const queryString = view === 'summary' ? '?view=summary' : '';
      const data = await apiFetch(`/v1/clients/${clientId}/missions${queryString}`);
      return this.transformApiData(data);
    } catch (error) {
      console.error(`Erreur récupération données client ${clientId}:`, error);
      throw error;
    }
  }

  /**
   * Transformer une mission brute API vers le format frontend Mission.
   * Applique les mêmes règles que `transformApiData` (duration_days, roi_data, team, etc.).
   */
  static transformMission(m: any): Mission {
    // Extraire les données du complete_json si disponibles
    const completeData = m.complete_json || {};
    const roiData = completeData.roi || m.roi_data || {};

    // Extraire les données d'analyse IA pour les idées de missions
    let ideaMissionData: any = {};
    let ideaRoiData: any = {};
    if (m.mission_type === "idea" && m.Informations_brutes) {
      try {
        const infoBrutes = typeof m.Informations_brutes === 'string'
          ? JSON.parse(m.Informations_brutes)
          : m.Informations_brutes;
        ideaMissionData = infoBrutes.ai_analysis || {};
        ideaRoiData = ideaMissionData.roi_data || {};
      } catch (e) {
        console.warn(`Erreur parsing informations_brutes pour mission ${m.id}:`, e);
      }
    }

    // Pour les idées de missions : inférer duration_days depuis duration_text si manquant
    let computedDurationDays: number = typeof m.duration_days === 'number' ? m.duration_days : Number(m.duration_days) || 0;
    let normalizedDuration = m.duration_text || m.duration;

    if (m.mission_type === "idea" && (!m.duration_days || m.duration_days === 0)) {
      const inferredDays = inferDurationDaysFromText(m.duration_text || m.duration);
      if (inferredDays !== null) {
        computedDurationDays = inferredDays;
        normalizedDuration = `${inferredDays} jours`;
      }
    }

    if (!computedDurationDays) {
      computedDurationDays = m.duration_days || 0;
    }

    const finalDuration = normalizedDuration || `${computedDurationDays} jours`;

    return {
      ...m,
      price: Number(m.price) || 0,
      duration_days: computedDurationDays,
      duration: finalDuration,
      team: m.team_size || m.team || "À définir",
      roi: roiData.roi || completeData.roi_pct_36m || "350%",
      timesSaved: roiData.timesSaved || completeData.hours_per_week || "Non spécifié",
      roiData: roiData,
      completeData: completeData,
      benefits_data: m.benefits_data,
      plan_data: m.plan_data,
      plan_detaille: m.plan_detaille,
      approach_data: m.approach_data,
      overview_data: m.overview_data,
      Analyse_cahier_des_charges: m.Analyse_cahier_des_charges,
      mission_month: m.mission_month,
      mission_year: m.mission_year,
      mission_principale: ideaMissionData.mission_principale || m.mission_principale,
      resultat_attendu: ideaMissionData.resultat_attendu || m.resultat_attendu,
      solution_capable_de: ideaMissionData.solution_capable_de || m.solution_capable_de,
      team_size: ideaMissionData.team_size || m.team_size,
      roi_data: Object.keys(ideaRoiData).length > 0 ? ideaRoiData : (m.roi_data || roiData),
      // Sous-missions (regroupement)
      grouping_mode: m.grouping_mode || 'solo',
      parent_mission_id: m.parent_mission_id || null,
      group_discount_percent: m.group_discount_percent ?? 15,
      sub_mission_order: m.sub_mission_order ?? 0,
      sub_mission_presentation: m.sub_mission_presentation || null,
      sub_missions: Array.isArray(m.sub_missions)
        ? m.sub_missions.map((sm: any) => ClientDataService.transformMission(sm))
        : [],
    };
  }

  /**
   * Transformer les données API vers le format frontend
   */
  static transformApiData(apiData: any): ClientData {
    // Transformer les missions pour compatibilité avec mockData
    const transformMissions = (missions: any[] = []) =>
      missions.map(m => ClientDataService.transformMission(m));

    return {
      id: apiData.id,
      title: apiData.title,
      client: apiData.client_name || apiData.client,
      status: apiData.status,
      createdAt: apiData.created_at || apiData.createdAt,
      lastUpdated: apiData.updated_at || apiData.lastUpdated,
      executive: apiData.executive_data || apiData.executive,
      foundations: transformMissions(apiData.foundations),
      missions: transformMissions(apiData.missions),
      additionalMissions: transformMissions(apiData.additional_missions || apiData.additionalMissions),
      options: apiData.options || [],
      categories: apiData.categories || [],
      pricingRules: apiData.pricing_rules || {},
      summary: apiData.summary_data || {},
      tjm: Number(apiData.tjm) || CLIENT_DATA_DEFAULTS.tjm,
      tjm_moe: Number(apiData.tjm_moe) || CLIENT_DATA_DEFAULTS.tjm_moe,
      annual_cost_percentage: apiData.annual_cost_percentage || CLIENT_DATA_DEFAULTS.annual_cost_percentage,
      project_management_percentage: apiData.project_management_percentage || CLIENT_DATA_DEFAULTS.project_management_percentage,
      project_duration_years: apiData.project_duration_years || CLIENT_DATA_DEFAULTS.project_duration_years,
      timeline_frise_enabled: apiData.timeline_frise_enabled !== false,
      planning_enabled: apiData.planning_enabled !== false,
      versions_enabled: apiData.versions_enabled !== false,
      roi_enabled: apiData.roi_enabled !== false,
      special_offer_enabled: apiData.special_offer_enabled === true,
      hide_category_badges: apiData.hide_category_badges === true,
      pack_ailix_enabled: apiData.pack_ailix_enabled === true,
      pack_ailix_config: apiData.pack_ailix_config || null,
      hourly_rate: apiData.hourly_rate || CLIENT_DATA_DEFAULTS.hourly_rate,
      mission_display_mode: apiData.mission_display_mode || CLIENT_DATA_DEFAULTS.mission_display_mode,
      idea_missions_display_mode: apiData.idea_missions_display_mode || CLIENT_DATA_DEFAULTS.idea_missions_display_mode,
      exceptional_discount_percentage: apiData.exceptional_discount_percentage || 0,
      missions_filters_defaults: apiData.missions_filters_defaults || {},
      homeStatistics: apiData.homeStatistics || {},
      disabled_volume_discounts: apiData.disabled_volume_discounts || {},
      cautious_planning_enabled: apiData.cautious_planning_enabled === true,
      cautious_planning_percentage: apiData.cautious_planning_percentage ?? 20,
      pilotage_enabled: apiData.pilotage_enabled === true,
      pilotage_nb_developers: apiData.pilotage_nb_developers ?? null,
      pilotage_start_month: apiData.pilotage_start_month ?? null,
      pilotage_start_year: apiData.pilotage_start_year ?? null,
      finalisation_nb_developers: apiData.finalisation_nb_developers ?? null,
      finalisation_start_month: apiData.finalisation_start_month ?? null,
      finalisation_start_year: apiData.finalisation_start_year ?? null,
      finalisation_offer_mode: apiData.finalisation_offer_mode || 'a_la_carte',
      finalisation_enabled_prestations: apiData.finalisation_enabled_prestations || {},
    };
  }

  /**
   * Récupérer les données de synthèse d'un client
   */
  static async getSynthesisData(clientId: string): Promise<SynthesisData> {
    try {
      const data = await apiFetch(`/v1/clients/${clientId}/synthesis`);
      return data;
    } catch (error) {
      console.error(`Erreur récupération synthèse ${clientId}:`, error);
      throw error;
    }
  }

  /**
   * Récupérer les détails complets d'une mission spécifique
   */
  static async getMissionDetails(clientId: string, missionId: string): Promise<any> {
    try {
      const data = await apiFetch(`/v1/clients/${clientId}/mission/${missionId}`);
      return data;
    } catch (error) {
      console.error(`Erreur récupération mission ${missionId}:`, error);
      throw error;
    }
  }

  /**
   * Calculer le pricing pour une sélection de missions et options
   */
  static async calculatePricing(clientId: string, request: PricingRequest): Promise<PricingResponse> {
    try {
      const data = await apiFetch(`/v1/clients/${clientId}/calculate-pricing`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request.selectedItems)
      });
      return data;
    } catch (error) {
      console.error(`Erreur calcul pricing pour ${clientId}:`, error);
      throw error;
    }
  }

  static async getPilotageSummary(clientId: string): Promise<PilotageSummaryResponse> {
    const data = await apiFetch(`/v1/clients/${clientId}/pilotage/summary`);
    return data as PilotageSummaryResponse;
  }

  static async getPilotageDashboard(
    clientId: string,
    itemId: string,
  ): Promise<PilotageDashboardResponse> {
    // encodeURIComponent : itemId provient de l'URL navigateur (useParams la décode) — un
    // segment forgé (%2F, %3F…) ne doit pas pouvoir réécrire le chemin d'API.
    const data = await apiFetch(
      `/v1/clients/${encodeURIComponent(clientId)}/pilotage/projet/${encodeURIComponent(itemId)}/dashboard`,
    );
    // Frontière Zod (spec run-003 §2.5) : payload non conforme → throw, capté par l'état error.
    const parsed = pilotageDashboardResponseSchema.safeParse(data);
    if (!parsed.success) {
      // Une dérive de contrat backend ne se résout pas par retry : logguer le champ fautif
      // pour le diagnostic (le client, lui, ne voit que l'état error neutre).
      console.error(`Contrat dashboard pilotage invalide (${clientId}/${itemId}):`, parsed.error);
      throw parsed.error;
    }
    return parsed.data;
  }

  static async createPilotageSummaryItem(
    clientId: string,
    payload: PilotageSummaryCreatePayload,
  ): Promise<PilotageSummaryItemMutationResponse> {
    const data = await apiFetch(`/v1/clients/${clientId}/pilotage/summary-items`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return data as PilotageSummaryItemMutationResponse;
  }

  static async updatePilotageSummaryItem(
    clientId: string,
    itemId: string,
    payload: PilotageSummaryUpdatePayload,
  ): Promise<PilotageSummaryItemMutationResponse> {
    const data = await apiFetch(`/v1/clients/${clientId}/pilotage/summary-items/${itemId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
    return data as PilotageSummaryItemMutationResponse;
  }

  static async createPilotageSummarySubitem(
    clientId: string,
    itemId: string,
    payload: PilotageSummarySubitemCreatePayload,
  ): Promise<PilotageSummarySubitemMutationResponse> {
    const data = await apiFetch(`/v1/clients/${clientId}/pilotage/summary-items/${itemId}/subitems`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return data as PilotageSummarySubitemMutationResponse;
  }

  static async updatePilotageSummarySubitem(
    clientId: string,
    itemId: string,
    subitemId: string,
    payload: PilotageSummarySubitemUpdatePayload,
  ): Promise<PilotageSummarySubitemMutationResponse> {
    const data = await apiFetch(`/v1/clients/${clientId}/pilotage/summary-items/${itemId}/subitems/${subitemId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
    return data as PilotageSummarySubitemMutationResponse;
  }

  // ─── Saisie manuelle Ailix du dashboard détail (run-003 Story 12) ────────────────────
  // Routes `Depends(require_ailix_employee)` (clients.py, entries). encodeURIComponent sur TOUS
  // les ids dynamiques (itemId vient de l'URL navigateur ; les ids ressource sortent de la
  // réponse dashboard). DELETE → 204 sans body (apiFetch renvoie null/undefined). Aucune
  // validation Zod sur la réponse write : la source de vérité reste le refetch dashboard
  // déclenché par l'invalidation appelante.

  static async createPilotageEntry(
    clientId: string,
    itemId: string,
    payload: PilotageEntryCreatePayload,
  ): Promise<unknown> {
    return apiFetch(
      `/v1/clients/${encodeURIComponent(clientId)}/pilotage/projet/${encodeURIComponent(itemId)}/entries`,
      { method: 'POST', body: JSON.stringify(payload) },
    );
  }

  static async updatePilotageEntry(
    clientId: string,
    itemId: string,
    entryId: string,
    payload: PilotageEntryUpdatePayload,
  ): Promise<unknown> {
    return apiFetch(
      `/v1/clients/${encodeURIComponent(clientId)}/pilotage/projet/${encodeURIComponent(itemId)}/entries/${encodeURIComponent(entryId)}`,
      { method: 'PATCH', body: JSON.stringify(payload) },
    );
  }

  static async deletePilotageEntry(
    clientId: string,
    itemId: string,
    entryId: string,
  ): Promise<unknown> {
    return apiFetch(
      `/v1/clients/${encodeURIComponent(clientId)}/pilotage/projet/${encodeURIComponent(itemId)}/entries/${encodeURIComponent(entryId)}`,
      { method: 'DELETE' },
    );
  }

  // Règles et questions (REFONDATION-PILOTAGE-LOT3-STORY-3.1) : mêmes conventions que les entries.
  // Pas de suppression : une règle se retire par son statut (trace exigée par Thomas le 2026-09-28).
  static async createPilotageRule(
    clientId: string,
    itemId: string,
    payload: PilotageRuleCreatePayload,
  ): Promise<unknown> {
    return apiFetch(
      `/v1/clients/${encodeURIComponent(clientId)}/pilotage/projet/${encodeURIComponent(itemId)}/rules`,
      { method: 'POST', body: JSON.stringify(payload) },
    );
  }

  static async updatePilotageRule(
    clientId: string,
    itemId: string,
    ruleId: string,
    payload: PilotageRuleUpdatePayload,
  ): Promise<unknown> {
    return apiFetch(
      `/v1/clients/${encodeURIComponent(clientId)}/pilotage/projet/${encodeURIComponent(itemId)}/rules/${encodeURIComponent(ruleId)}`,
      { method: 'PATCH', body: JSON.stringify(payload) },
    );
  }
}

/**
 * Hook React pour récupérer les données client
 */
export const useClientData = (clientId: string, view: 'full' | 'summary' = 'full') => {
  const queryClient = useQueryClient();
  const queryKey = clientDataKeys.byClient(clientId, view);

  const query = useQuery({
    queryKey,
    queryFn: () => ClientDataService.getClientData(clientId, view),
    enabled: Boolean(clientId),
    ...CLIENT_DATA_QUERY_CONFIG,
  });

  const refreshMission = React.useCallback(async (missionId?: string) => {
    if (!clientId) return;

    if (!missionId) {
      await query.refetch();
      return;
    }

    try {
      const rawMission = await ClientDataService.getMissionDetails(clientId, missionId);
      const updatedMission = ClientDataService.transformMission(rawMission);
      queryClient.setQueryData(clientDataKeys.missionDetail(clientId, missionId), updatedMission);

      const mergeMission = (prevData: ClientData | null | undefined): ClientData | null | undefined => {
        if (!prevData || typeof prevData !== 'object') return prevData;

        let updated = false;
        const updateMission = (mission: Mission) => {
          if (mission?.id !== missionId) return mission;
          updated = true;
          return { ...mission, ...updatedMission };
        };

        const nextData: ClientData = {
          ...prevData,
          missions: Array.isArray(prevData.missions) ? prevData.missions.map(updateMission) : prevData.missions,
          foundations: Array.isArray(prevData.foundations) ? prevData.foundations.map(updateMission) : prevData.foundations,
          additionalMissions: Array.isArray(prevData.additionalMissions)
            ? prevData.additionalMissions.map(updateMission)
            : prevData.additionalMissions,
        };

        if (!updated) {
          console.warn('⚠️ Mission not found during refresh:', missionId);
        }
        return nextData;
      };

      queryClient.setQueryData<ClientData | null | undefined>(clientDataKeys.byClient(clientId, view), mergeMission);

      // L'autre vue est invalidée pour éviter d'injecter des champs "full" dans un cache "summary".
      const alternateView = view === 'full' ? 'summary' : 'full';
      queryClient.invalidateQueries({ queryKey: clientDataKeys.byClient(clientId, alternateView) });
    } catch (error) {
      console.error('🚨 ERREUR CRITIQUE refreshMission:', error);
    }
  }, [clientId, query, queryClient, view]);

  return {
    data: query.data ?? null,
    loading: query.isLoading,
    error: query.error instanceof Error ? query.error.message : (query.error ? 'Erreur inconnue' : null),
    refetch: query.refetch,
    refreshMission,
  };
};

/**
 * Hook React pour récupérer les données de synthèse
 */
export const useSynthesisData = (clientId: string) => {
  const query = useQuery({
    queryKey: clientDataKeys.synthesis(clientId),
    queryFn: () => ClientDataService.getSynthesisData(clientId),
    enabled: Boolean(clientId),
    ...SYNTHESIS_QUERY_CONFIG,
  });

  return {
    data: query.data ?? null,
    loading: query.isLoading,
    error: query.error instanceof Error ? query.error.message : (query.error ? 'Erreur inconnue' : null),
    refetch: query.refetch,
  };
};

/**
 * Hook React Query dédié au détail mission (chargement à la demande).
 */
export const useMissionDetail = (clientId: string, missionId?: string | null) => {
  const query = useQuery({
    queryKey: clientDataKeys.missionDetail(clientId, missionId || ''),
    queryFn: () => ClientDataService.getMissionDetails(clientId, missionId as string),
    enabled: Boolean(clientId && missionId),
    ...CLIENT_DATA_QUERY_CONFIG,
  });

  return {
    data: query.data ?? null,
    loading: query.isLoading,
    error: query.error instanceof Error ? query.error.message : (query.error ? 'Erreur inconnue' : null),
    refetch: query.refetch,
  };
};
